import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
} from '@angular/core';
import {
  BD_EXPORT_AS_FILE_DEFAULT_CONFIG,
  BdExportAsFileConfig,
} from './export.config';
import { BdExportAsFileData } from '@bindoc-projects/util-domain-lib';
import { BdExportAsFileDialogResult } from './dialog/dialog.component';
import { BdCurrentUserService } from '@bindoc-projects/user-lib/data';
import { BdDataTestDirective } from '@bindoc-projects/util-lib/ui';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { openBdExportAsFileDialog } from './open-export-dialog';
import { bdExportAsFileNotify } from './export-feedback';
import { BD_DIALOG_SERVICE_PROVIDER } from '@bindoc-projects/ui-lib/dialog/ui';

@Component({
  selector: 'bd-export-as-file',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-toast position="bottom-center" />
    @if (showTrigger()) {
      <p-button
        icon="pi pi-download"
        severity="secondary"
        [rounded]="true"
        [text]="true"
        [disabled]="!data()"
        [data-test]="exportConfig().trigger.dataTestKey"
        [pTooltip]="exportConfig().trigger.tooltipText"
        [ariaLabel]="exportConfig().trigger.tooltipText"
        (onClick)="onClick()"
      />
    }
  `,
  imports: [Button, Tooltip, ToastModule, BdDataTestDirective],
  providers: [BD_DIALOG_SERVICE_PROVIDER, MessageService],
})
export class BdExportAsFileComponent {
  private readonly _dialogService = inject(DialogService);
  private readonly _messageService = inject(MessageService);
  private readonly _currentUserService = inject(BdCurrentUserService);
  private readonly _destroyRef = inject(DestroyRef);

  public readonly data = input<BdExportAsFileData>();
  public readonly filename = input<string | null>(null);
  public readonly config = input<BdExportAsFileConfig>(
    BD_EXPORT_AS_FILE_DEFAULT_CONFIG,
  );

  public readonly showTrigger = signal(false);
  public readonly exportConfig = computed<BdExportAsFileConfig>(() =>
    structuredClone(this.config() ?? BD_EXPORT_AS_FILE_DEFAULT_CONFIG),
  );

  private _authenticated = false;

  constructor() {
    this._currentUserService.authenticated$
      .pipe(takeUntilDestroyed())
      .subscribe((authenticated) => {
        this._authenticated = authenticated;
        this._updateShowTrigger(authenticated);
      });
  }

  public onClick(): void {
    // Never open a dialog that would snapshot `undefined` data — the trigger is
    // also `[disabled]` without data, this guards the same rule on click (TEST-218).
    const data = this.data();
    if (!data) {
      return;
    }

    openBdExportAsFileDialog(this._dialogService, {
      data,
      filename: this.filename(),
      config: this.config(),
      authenticated: this._authenticated,
    })
      .onClose.pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((result?: BdExportAsFileDialogResult) => {
        if (!result) {
          return;
        }
        this._notify(result);
      });
  }

  /**
   * Terminal feedback for the export request — a toast replaces the former
   * in-dialog error screen. The wording lives in
   * {@link bdExportAsFileNotify} so every trigger raises the same one.
   */
  private _notify(result: BdExportAsFileDialogResult): void {
    bdExportAsFileNotify(this._messageService, result);
  }

  private _updateShowTrigger(authenticated: boolean): void {
    if (
      this.exportConfig().authenticationStrategy === 'always' &&
      !authenticated
    ) {
      this.showTrigger.set(false);
      return;
    }
    this.showTrigger.set(true);
  }
}
